import { memo } from 'react';
import { ShieldCheck } from 'lucide-react';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { formatDurationSeconds, formatPlayers } from '../../utils/starchart';
import type { StarchartServerStats } from '../../types/starchart';

interface ReliabilityCardProps {
  stats: StarchartServerStats | null;
}

const Metric = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) => (
  <div className="subcard p-3">
    <div className="eyebrow">{label}</div>
    <div className="text-lg font-semibold text-white tabular-nums mt-0.5">{value}</div>
    <div className="text-xs text-glass-500 mt-0.5">{hint}</div>
  </div>
);

const formatScore = (value: number | null): string =>
  value === null || !isFinite(value) ? '—' : value.toFixed(1);

/**
 * Lifetime reliability, from an endpoint the standalone site never called.
 *
 * These are whole-history figures rather than range-scoped ones, so they answer a different
 * question from the charts above: not "what happened this week" but "what kind of server is
 * this". Every field is nullable, and an unknown server returns all nulls rather than a 404.
 */
export const ReliabilityCard = memo(({ stats }: ReliabilityCardProps) => {
  const hasData = (stats?.snapshot_count ?? 0) > 0;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Lifetime reliability"
        icon={ShieldCheck}
        description="Across all history StarChart holds for this server, not just the selected range."
      />

      {!hasData ? (
        <EmptyState
          bare
          compact
          icon={ShieldCheck}
          title="No lifetime data"
          description="StarChart has never recorded a session for this server."
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
          <Metric
            label="Consistency"
            value={formatScore(stats?.consistency_score ?? null)}
            hint="How steadily it carries players"
          />
          <Metric
            label="Downtime score"
            value={formatScore(stats?.downtime_score ?? null)}
            hint="Lower is better"
          />
          <Metric
            label="Average session"
            value={formatDurationSeconds(stats?.session_length_score ?? null)}
            hint="How long players stay"
          />
          <Metric
            label="Sessions"
            value={(stats?.session_count ?? 0).toLocaleString()}
            hint="Player visits recorded"
          />
          <Metric
            label="Total play time"
            value={formatDurationSeconds(stats?.total_session_time ?? null)}
            hint="Summed across all sessions"
          />
          <Metric
            label="All-time peak"
            value={formatPlayers(stats?.peak_players ?? null)}
            hint={`Average ${formatPlayers(stats?.avg_players ?? null)} · ${(
              stats?.snapshot_count ?? 0
            ).toLocaleString()} snapshots`}
          />
        </div>
      )}
    </div>
  );
});

ReliabilityCard.displayName = 'ReliabilityCard';
